/**
 * Who to put in an opening, ranked.
 *
 * Pure, like `scheduleGrid`. The population comes from the Action Center
 * queues, which are already the people the practice decided are worth chasing,
 * so this ranks rather than re-deciding.
 */

/** Where a candidate came from, which is also what chair they can sit in. */
export type CandidateSource = `hygiene` | `treatment`

export type SlotCandidate = {
  id: string
  name: string
  source: CandidateSource
  /** The band or queue they came from, in the practice's own words. */
  sourceLabel: string
  /** Production this booking is worth, when the practice has a figure for it. */
  value?: number
  /** How long they have been waiting: overdue for hygiene, or since presented. */
  waitingDays?: number
  contactId?: string
  phone?: string
  /** One line of why this person, for the row. */
  detail?: string
}

/**
 * What kind of work an opening can take.
 *
 * The availability engine answers per appointment category, and the four it
 * knows do not split evenly. Emergency, consult and limited exam are all the
 * dentist's short appointments, so they suit a treatment case. **General** is
 * the practice's default appointment type, which excludes nobody: it is what
 * the agent books when there is no special reason, so a cleaning and a crown
 * consult are both plausible in it.
 *
 * Nothing the endpoint returns is hygiene-specific. An earlier reading treated
 * the unflagged probe as a hygienist's chair, which would have sent recall
 * patients at times the practice never said would take them.
 */
export type OpeningKind = `hygiene` | `treatment` | `either` | `unknown`

const TREATMENT_TYPES = new Set([`limitedExam`, `emergency`, `consult`])
/** The default type: it names no restriction, so it rules nobody out. */
const OPEN_TO_ANYONE = new Set([`general`])

export function openingKind(types: string[]): OpeningKind {
  const anyone = types.some((type) => OPEN_TO_ANYONE.has(type))
  const treatment = types.some((type) => TREATMENT_TYPES.has(type))
  if (anyone) return `either`
  if (treatment) return `treatment`
  return `unknown`
}

/**
 * Can this person be offered this opening?
 *
 * A cleaning cannot happen in the dentist's consult chair and a crown cannot
 * happen in the hygienist's, so offering the wrong one wastes both the opening
 * and the phone call. An opening whose type nobody recognises offers everybody:
 * the practice can read what it is for, and hiding the whole list behind an
 * unknown type name would be less useful than showing it.
 */
export function fitsOpening(candidate: SlotCandidate, kind: OpeningKind): boolean {
  if (kind === `either` || kind === `unknown`) return true
  return candidate.source === kind
}

/** How many people a panel can offer before it stops being a decision. */
const SHORTLIST = 12

export type RankedCandidates = {
  /** The shortlist, best first. */
  fitting: SlotCandidate[]
  /** Fitting people who did not make the shortlist. */
  moreCount: number
  /** People in the queues who cannot take this opening at all. */
  otherCount: number
}

/**
 * The order the design settled on: fit, then production, then patience.
 *
 * Production before patience because an opening is an hour of the practice's
 * capacity and the point of filling it is what it produces. Patience second so
 * that among cases worth the same, the person who has been waiting longest
 * stops being overtaken forever.
 */
export function rankCandidates(candidates: SlotCandidate[], kind: OpeningKind): RankedCandidates {
  const fitting = candidates.filter((candidate) => fitsOpening(candidate, kind))
  const sorted = [...fitting].sort((a, b) => {
    const aPriced = a.value != null
    const bPriced = b.value != null
    // A missing figure is not a zero. Priced cases lead, and the unpriced stay
    // ordered among themselves by how long they have waited rather than by
    // whatever order they were fetched in.
    if (aPriced !== bPriced) return aPriced ? -1 : 1
    if (aPriced && bPriced && a.value !== b.value) return (b.value ?? 0) - (a.value ?? 0)
    return (b.waitingDays ?? 0) - (a.waitingDays ?? 0)
  })
  return {
    fitting: sorted.slice(0, SHORTLIST),
    moreCount: Math.max(0, sorted.length - SHORTLIST),
    otherCount: candidates.length - fitting.length,
  }
}
